import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { z } from 'zod';

const PILLAR_COLORS: Record<string, string> = {
  critical_minerals: 'bg-amber-100 text-amber-800 border-amber-300',
  industrialization: 'bg-blue-100 text-blue-800 border-blue-300',
  infrastructure: 'bg-green-100 text-green-800 border-green-300',
  governance: 'bg-purple-100 text-purple-800 border-purple-300',
};

const PILLARS = ['critical_minerals', 'industrialization', 'infrastructure', 'governance'];
const AUDIENCES = ['Diaspora', 'SME', 'Investor', 'Youth', 'Academic', 'Government', 'Other'];

const schema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
});

interface Program {
  id: string;
  title: string;
  description: string;
  spa_pillar: string | null;
  target_audience: string[] | null;
  country: string | null;
  deadline: string | null;
  link_url: string | null;
  created_at: string | null;
}

const emptyForm = {
  title: '',
  description: '',
  spa_pillar: '',
  target_audience: [] as string[],
  country: '',
  deadline: '',
  link_url: '',
};

export function ProgramsTab() {
  const { t } = useTranslation();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchPrograms(); }, []);

  const fetchPrograms = async () => {
    setLoading(true);
    const { data } = await supabase.from('spa_programs').select('*').order('created_at', { ascending: false });
    setPrograms((data as unknown as Program[]) || []);
    setLoading(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (p: Program) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      description: p.description,
      spa_pillar: p.spa_pillar || '',
      target_audience: (p.target_audience as string[]) || [],
      country: p.country || '',
      deadline: p.deadline || '',
      link_url: p.link_url || '',
    });
    setErrors({});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const result = schema.safeParse(form);
    if (!result.success) {
      const e: Record<string, string> = {};
      result.error.issues.forEach(i => { e[i.path[0] as string] = i.message; });
      setErrors(e);
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      description: form.description,
      spa_pillar: form.spa_pillar || null,
      target_audience: form.target_audience.length ? form.target_audience : null,
      country: form.country || null,
      deadline: form.deadline || null,
      link_url: form.link_url || null,
    };

    let error;
    if (editingId) {
      ({ error } = await supabase.from('spa_programs').update(payload).eq('id', editingId));
    } else {
      ({ error } = await supabase.from('spa_programs').insert(payload));
    }
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(editingId ? 'Program updated' : 'Program created');
    setDialogOpen(false);
    fetchPrograms();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from('spa_programs').delete().eq('id', deleteId);
    if (error) { toast.error(error.message); return; }
    toast.success('Program deleted');
    setDeleteId(null);
    fetchPrograms();
  };

  const toggleAudience = (val: string) => {
    setForm(f => ({
      ...f,
      target_audience: f.target_audience.includes(val)
        ? f.target_audience.filter(v => v !== val)
        : [...f.target_audience, val],
    }));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('admin.tabs.programs')}</CardTitle>
        <Button onClick={openCreate} size="sm"><Plus className="mr-1 h-4 w-4" />{t('admin.addProgram')}</Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : programs.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">{t('programs.noResults')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Pillar</TableHead>
                <TableHead>Target Audience</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {programs.map(p => {
                const audience = (p.target_audience as string[]) || [];
                return (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.title}</TableCell>
                    <TableCell>
                      {p.spa_pillar ? (
                        <Badge variant="outline" className={PILLAR_COLORS[p.spa_pillar] || ''}>
                          {t(`spaPillars.${p.spa_pillar}`)}
                        </Badge>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {audience.slice(0, 3).map(a => (
                          <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>
                        ))}
                        {audience.length > 3 && <Badge variant="secondary" className="text-xs">+{audience.length - 3}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {p.deadline ? format(new Date(p.deadline), 'MMM d, yyyy') : <span className="text-muted-foreground">Open</span>}
                    </TableCell>
                    <TableCell>{p.created_at ? format(new Date(p.created_at), 'MMM d, yyyy') : ''}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteId(p.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Program' : t('admin.addProgram')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} />
              {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
            </div>
            <div>
              <Label>Pillar</Label>
              <Select value={form.spa_pillar} onValueChange={v => setForm(f => ({ ...f, spa_pillar: v }))}>
                <SelectTrigger><SelectValue placeholder="None selected" /></SelectTrigger>
                <SelectContent>
                  {PILLARS.map(p => (
                    <SelectItem key={p} value={p}>{t(`spaPillars.${p}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Target Audience</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {AUDIENCES.map(a => (
                  <label key={a} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.target_audience.includes(a)} onCheckedChange={() => toggleAudience(a)} />
                    {a}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Country</Label>
              <Input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
            </div>
            <div>
              <Label>Deadline</Label>
              <Input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
            </div>
            <div>
              <Label>Link URL</Label>
              <Input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} placeholder="https://..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.delete')}</AlertDialogTitle>
            <AlertDialogDescription>{t('admin.confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
